import { el } from '../dom.js';
import type { I18n } from '../i18n.js';
import { api } from '../session.js';

/**
 * §2a v2.3 — optional Identity form. Three dropdowns, all with "prefer not to
 * say" as the default (we never coerce disclosure). Submission is idempotent;
 * each subsequent submit overwrites the session record.
 *
 * Rendered as a standalone page at `#/identity` and as an inline section on
 * the privacy page (for mid-session opt-in). Does NOT block any other flow.
 */

const RELIGIONS = [
  'prefer_not_to_say',
  'christian', 'muslim', 'jewish', 'hindu', 'buddhist',
  'nonreligious', 'other',
];

const ETHNICITIES = [
  'prefer_not_to_say',
  'northern_africa', 'sub_saharan_africa', 'western_asia', 'central_asia',
  'southern_asia', 'eastern_asia', 'south_eastern_asia', 'oceania',
  'northern_america', 'latin_america', 'europe', 'indigenous', 'other',
];

// Minimal ISO-like suggestion list; players can free-type any valid 2-letter code.
const CITIZENSHIP_COMMON = [
  'prefer_not_to_say', 'stateless',
  'US', 'GB', 'DE', 'FR', 'ES', 'IT', 'PL', 'UA', 'RU', 'BR', 'MX', 'CN', 'IN',
  'JP', 'SA', 'EG', 'NG', 'ZA', 'KE', 'AU', 'CA',
];

export interface IdentityStepProps {
  i18n: I18n;
  onDone: () => void;
  onSkip: () => void;
}

export function IdentityStep({ i18n, onDone, onSkip }: IdentityStepProps): HTMLElement {
  const section = el('section', { class: 'identity-step', 'aria-label': i18n.t('identity.heading') });

  section.appendChild(el('h1', {}, i18n.t('identity.heading')));
  section.appendChild(el('p', { class: 'lede' }, i18n.t('identity.intro')));
  // DR-UX-4 — bridging copy: make the "NOT used to personalise scenarios"
  // reassurance unavoidable, not buried in privacy note. Emphasised so
  // anxious readers (‘why do you want my religion?’) see it at eye level.
  section.appendChild(el('p', { class: 'identity-reassurance' }, i18n.t('identity.use_clarification')));
  section.appendChild(el('p', { class: 'note' }, i18n.t('identity.privacy_note')));

  const religionSelect = el('select', { id: 'identity-religion' }) as HTMLSelectElement;
  for (const r of RELIGIONS) {
    religionSelect.appendChild(el('option', { value: r }, i18n.t(`identity.religion.${r}`)));
  }
  religionSelect.value = 'prefer_not_to_say';

  const ethnicitySelect = el('select', { id: 'identity-ethnicity' }) as HTMLSelectElement;
  for (const e of ETHNICITIES) {
    ethnicitySelect.appendChild(el('option', { value: e }, i18n.t(`identity.ethnicity.${e}`)));
  }
  ethnicitySelect.value = 'prefer_not_to_say';

  const citizenshipSelect = el('select', { id: 'identity-citizenship' }) as HTMLSelectElement;
  for (const c of CITIZENSHIP_COMMON) {
    const label =
      c === 'prefer_not_to_say' ? i18n.t('identity.citizenship.prefer_not_to_say') :
      c === 'stateless' ? i18n.t('identity.citizenship.stateless') :
      c;
    citizenshipSelect.appendChild(el('option', { value: c }, label));
  }
  citizenshipSelect.value = 'prefer_not_to_say';

  section.appendChild(el('div', { class: 'identity-form' }, [
    el('label', { for: 'identity-religion' }, i18n.t('identity.religion.label')),
    religionSelect,
    el('label', { for: 'identity-ethnicity' }, i18n.t('identity.ethnicity.label')),
    ethnicitySelect,
    el('label', { for: 'identity-citizenship' }, i18n.t('identity.citizenship.label')),
    citizenshipSelect,
  ]));

  const submitBtn = el('button', { class: 'block', type: 'button' }, i18n.t('identity.submit')) as HTMLButtonElement;
  const skipBtn = el('button', { class: 'ghost', type: 'button' }, i18n.t('identity.skip')) as HTMLButtonElement;

  submitBtn.addEventListener('click', async () => {
    submitBtn.disabled = true;
    skipBtn.disabled = true;
    try {
      const body: Record<string, string> = {};
      if (religionSelect.value) body.religion = religionSelect.value;
      if (ethnicitySelect.value) body.ethnicity = ethnicitySelect.value;
      if (citizenshipSelect.value) body.citizenship_iso = citizenshipSelect.value;
      await api.submitIdentity(body);
      onDone();
    } catch {
      submitBtn.disabled = false;
      skipBtn.disabled = false;
    }
  });
  skipBtn.addEventListener('click', () => onSkip());

  section.appendChild(el('div', { class: 'actions' }, [
    el('div', { class: 'actions-primary' }, [submitBtn]),
    el('div', { class: 'actions-secondary' }, [skipBtn]),
  ]));

  return section;
}
